import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Check, Loader2, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_TRIES = 8;

const LABELS = {
  cv_edit: "The CV Edit",
  from_scratch: "CV From Scratch",
  power_pair: "CV + LinkedIn Power Pair",
};

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const quoteToken = params.get("quote");
  const [state, setState] = useState("checking"); // checking | paid | failed | timeout | error
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let timer = null;
    const run = async (tries) => {
      if (cancelled) return;
      if (!sessionId) {
        setState("error");
        return;
      }
      try {
        const res = quoteToken
          ? await axios.get(`${API}/quotes/${quoteToken}/payment/${sessionId}`)
          : await axios.get(`${API}/payments/status/${sessionId}`);
        if (cancelled) return;
        if (res.data.payment_status === "paid") {
          setDetail(res.data);
          setState("paid");
          return;
        }
        if (res.data.payment_status === "failed") {
          setState("failed");
          return;
        }
        if (tries >= MAX_TRIES) {
          setState("timeout");
          return;
        }
        timer = setTimeout(() => run(tries + 1), 2000);
      } catch (e) {
        if (!cancelled) setState("error");
      }
    };
    run(0);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId, quoteToken]);

  return (
    <main className="min-h-screen bg-[#0A0A0A] text-[#F8F8F5] flex items-center justify-center px-6">
      <div className="noise-overlay" aria-hidden="true" />
      <motion.div
        data-testid="payment-success-page"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="max-w-lg w-full text-center"
      >
        {state === "checking" && (
          <>
            <Loader2 className="animate-spin text-[#DFFF00] mx-auto" size={44} />
            <h1 className="font-display text-3xl font-bold tracking-tight mt-8">Confirming your payment…</h1>
            <p className="font-body text-[#A1A1AA] mt-3">One moment while we verify with Stripe.</p>
          </>
        )}

        {state === "paid" && (
          <>
            <div className="w-16 h-16 rounded-full bg-[#DFFF00] flex items-center justify-center mx-auto">
              <Check className="text-[#0A0A0A]" size={30} />
            </div>
            <h1 className="font-display text-4xl font-bold tracking-tighter mt-8">Payment confirmed.</h1>
            <p className="font-body text-[#A1A1AA] mt-4 leading-relaxed">
              {quoteToken ? (
                <>
                  Thank you — your <span className="text-[#F8F8F5] font-semibold">{detail?.quote?.service_label || "service"}</span>{" "}
                  (order {detail?.quote?.reference}) is confirmed. A receipt and confirmation email are on their way.
                  Your order page explains what happens next and lets you send your documents securely.
                </>
              ) : (
                <>
                  Thank you — your <span className="text-[#F8F8F5] font-semibold">{LABELS[detail?.lookup_key] || "package"}</span>{" "}
                  is booked. We'll email you within 24 hours to kick things off. Check your inbox (and spam) for a receipt.
                </>
              )}
            </p>
            <button
              data-testid="success-home-btn"
              onClick={() => navigate(quoteToken ? `/quote/${quoteToken}` : "/")}
              className="mt-10 rounded-full bg-[#DFFF00] text-[#0A0A0A] font-body font-semibold px-8 py-4 hover:bg-[#C8E600] transition-colors"
            >
              {quoteToken ? "Go to your order and next steps" : "Back to home"}
            </button>
          </>
        )}

        {state === "failed" && (
          <>
            <div className="w-16 h-16 rounded-full border border-[#27272A] flex items-center justify-center mx-auto">
              <AlertTriangle className="text-[#DFFF00]" size={26} />
            </div>
            <h1 className="font-display text-3xl font-bold tracking-tight mt-8">Payment didn't complete.</h1>
            <p className="font-body text-[#A1A1AA] mt-4 leading-relaxed">
              Nothing was charged. You can safely try again, or contact us if you keep having trouble.
            </p>
            <button
              data-testid="failed-retry-btn"
              onClick={() => navigate(quoteToken ? `/quote/${quoteToken}` : "/services")}
              className="mt-10 rounded-full bg-[#DFFF00] text-[#0A0A0A] font-body font-semibold px-8 py-4 hover:bg-[#C8E600] transition-colors"
            >
              {quoteToken ? "Return to your quote to try again" : "Back to services"}
            </button>
          </>
        )}

        {(state === "timeout" || state === "error") && (
          <>
            <div className="w-16 h-16 rounded-full border border-[#27272A] flex items-center justify-center mx-auto">
              <AlertTriangle className="text-[#DFFF00]" size={26} />
            </div>
            <h1 className="font-display text-3xl font-bold tracking-tight mt-8">
              {state === "timeout" ? "Still processing…" : "We couldn't verify this."}
            </h1>
            <p className="font-body text-[#A1A1AA] mt-4 leading-relaxed">
              If you completed payment, you'll still receive a confirmation email shortly. Otherwise you can try again.
            </p>
            <button
              data-testid="error-home-btn"
              onClick={() => navigate("/")}
              className="mt-10 rounded-full border border-[#27272A] text-[#F8F8F5] font-body px-8 py-4 hover:border-[#DFFF00] transition-colors"
            >
              Back to home
            </button>
          </>
        )}
      </motion.div>
    </main>
  );
}
